import { useState } from "react";
import { useAdminCampaigns } from "@/hooks/useAdminCampaigns";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Plus, Edit, Trash2 } from "lucide-react";
import { format } from "date-fns";
import CampaignForm from "./CampaignForm";
import CampaignPackagesList from "./CampaignPackagesList";
import { CampaignStatsCard } from "./campaigns/CampaignStatsCard";
import CampaignPurchasesList from "./campaigns/CampaignPurchasesList";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Campaign } from "@/hooks/useActiveCampaign";

type CampaignStatus = {
  label: string;
  variant: "default" | "secondary" | "destructive" | "outline";
  className?: string;
};

const getStatus = (campaign: Campaign): CampaignStatus => {
  const now = new Date();
  const start = new Date(campaign.start_date);
  const end = new Date(campaign.end_date);

  if (!campaign.is_active) return { label: "Disabled", variant: "secondary", className: "bg-muted text-muted-foreground" };
  if (now < start) return { label: "Scheduled", variant: "outline", className: "border-warning text-warning" };
  if (now > end) return { label: "Expired", variant: "outline", className: "text-muted-foreground" };
  return { label: "Enabled", variant: "default", className: "bg-success text-success-foreground" };
};

const CampaignsList = () => {
  const { campaigns, isLoading, deleteCampaign } = useAdminCampaigns();
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleEdit = (campaign: Campaign) => {
    setEditingCampaign(campaign);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (deletingId) {
      await deleteCampaign.mutateAsync(deletingId);
      setDeletingId(null);
    }
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingCampaign(null);
  };

  if (showForm) {
    return (
      <CampaignForm
        campaign={editingCampaign}
        onClose={handleCloseForm}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Card */}
      <CampaignStatsCard />

      {/* Purchases List */}
      <CampaignPurchasesList />

      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">Campaigns</h2>
        <Button onClick={() => setShowForm(true)} className="gap-2">
          <Plus className="h-4 w-4" />
          New Campaign
        </Button>
      </div>

      {isLoading ? (
        <div className="text-center py-8 text-muted-foreground">Loading...</div>
      ) : campaigns.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-muted-foreground mb-4">No campaigns yet</p>
          <Button onClick={() => setShowForm(true)}>Create First Campaign</Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {campaigns.map((campaign) => {
            const status = getStatus(campaign);
            return (
              <Card key={campaign.id} className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-lg">{campaign.name}</h3>
                      <Badge 
                        variant={status.variant}
                        className={status.className}
                      >
                        {status.label}
                      </Badge>
                      {campaign.is_hidden && (
                        <Badge variant="outline" className="text-xs">Hidden</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">
                      {format(new Date(campaign.start_date), "MMM d, yyyy")} -{" "}
                      {format(new Date(campaign.end_date), "MMM d, yyyy")}
                    </p>
                    {campaign.is_hidden && (
                      <p className="text-xs text-muted-foreground font-mono mb-1">
                        app.nimi.live/offers/{campaign.slug}
                      </p>
                    )}
                    {campaign.description && (
                      <p className="text-sm text-muted-foreground">
                        {campaign.description}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(campaign)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setDeletingId(campaign.id)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t">
                  <CampaignPackagesList campaignId={campaign.id} />
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <AlertDialog open={!!deletingId} onOpenChange={() => setDeletingId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Campaign?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this campaign and all its packages.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CampaignsList;
